import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { format, isValid, parse, subMonths } from 'date-fns';
import Page from '../../core/components/Page';
import ChartCard from '../components/ChartCard';
import TextChart from '../components/TextChart';
import axios from 'axios';
import FilterDropdown, { FilterGroup, TriggerButtonIndicator } from '../../core/components/Filter.tsx';
import {
    useAssistanceReasons,
    useDailyMetrics,
    useFilterOptions,
    useSmartInboxIntegrations,
} from '../utils/dataByDocumentType.ts';
import { Trans, useTranslation } from 'react-i18next';
import Layout from '../../core/components/Layout';
import { withIcon } from '../../core/components/Icon';
import {
    faArrowDownToLine,
    faBolt,
    faBoltSlash,
    faCopy,
    faInbox,
    faSpinnerThird,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { DateRangeForm, formatDateRange, getSelectedOption } from '../components/DateRangeFilter';
import { useApplicationContext } from '../../core/contexts/ApplicationContext';
import { canDownloadAnalytics } from '../../users/utils';
import { DOWNLOAD_KPI_DATA_URL, DOWNLOAD_SMART_INBOX_ANALYTICS_DATA } from '../constants';
import { isoDateFormat } from '../../core/utils/dates.ts';
import Button from '../../core/components/Button';
import classnames from '../../core/utils/classnames.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import { defaultDateFormat } from '../../core/components/Fields/DateField.tsx';
import useStickyState from '../../core/utils/useStickyState.tsx';
import TabButton from '../../core/components/TabButton.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import DocumentProcessingCard from '../components/DocumentProcessingCard.tsx';
import { useSearchParams } from 'react-router-dom';
import TimeSpentAssistingDocumentsCard from '../components/TimeSpentAssistingDocumentsCard.tsx';
import { secondsToHumanReadable } from '../utils/chartUtils.ts';
import FieldExtractionCard from '../components/FieldExtractionCard.tsx';
import useSearchParamState, { useArraySearchParamState } from '../../core/utils/useSearchParamState.tsx';
import AssistanceReasonsCard from '../components/AssistanceReasonsCard.tsx';
import { useDataRetentionMinDate } from '../utils/dataRetention.ts';
import median from '../../core/utils/median.ts';
import InboxAnalyticsContent from '../components/InboxAnalyticsContent.tsx';

interface ChartProps {
    data: any[];
    [key: string]: any;
}

const AutomationEnabledIcon = withIcon(faBolt);
const AutomationDisabledIcon = withIcon(faBoltSlash);
const ClearIcon = withIcon(faXmark);
const ArrowDownToLineIcon = withIcon(faArrowDownToLine);
const LoadingIcon = withIcon(faSpinnerThird);
const WorkbenchIcon = withIcon(faCopy);
const InboxIcon = withIcon(faInbox);

const DocumentTypeFilterGroup = ({
    options,
    selected,
    onSelectedChange,
    channelOptions,
    selectedChannels,
    onSelectedChannelsChange,
}: {
    options: { value: string; label: string; icon?: React.ComponentType; disabled?: boolean }[];
    selected: string[];
    onSelectedChange: (value: string[]) => void;
    channelOptions: { value: string; label: string }[];
    selectedChannels: string[];
    onSelectedChannelsChange: (value: string[]) => void;
}) => {
    const { t } = useTranslation('analytics');

    return (
        <FilterGroup>
            {options.map((option) => {
                const isActive = selected.includes(option.value);
                const Icon = option.icon || null;

                if (isActive) {
                    return (
                        <FilterDropdown
                            key={option.value}
                            allLabel={`${option.label}: ${t('dashboard.filters.channel.all')}`}
                            label={option.label}
                            options={channelOptions}
                            selected={selectedChannels}
                            onSelectedChange={onSelectedChannelsChange}
                            renderGroupName={(groupName: string) => (
                                <div className="flex gap-1.5 items-center">
                                    {groupName === 'automationEnabled' ? (
                                        <>
                                            <span>{t('dashboard.filters.channel.automationEnabled')}</span>
                                            <AutomationEnabledIcon />
                                        </>
                                    ) : (
                                        <>
                                            <span>{t('dashboard.filters.channel.automationDisabled')}</span>
                                            <AutomationDisabledIcon />
                                        </>
                                    )}
                                </div>
                            )}
                            icon={<Icon />}
                            className="rounded-none -mr-px last:mr-0 first:rounded-l last:rounded-r z-20"
                            multiple
                            active
                        />
                    );
                }

                return (
                    <Tooltip
                        content={
                            <Trans
                                t={t}
                                i18nKey={
                                    option.disabled
                                        ? 'dashboard.filters.documentTypeDisabled'
                                        : `dashboard.filters.documentType.${option.value}`
                                }
                                values={{
                                    documentType: t(`dashboard.filters.documentType.${option.value}`),
                                }}
                                components={{
                                    strong: <strong className="font-medium" />,
                                }}
                            />
                        }
                        key={option.value}
                        sideOffset={5}
                        long
                    >
                        <FilterGroup.Button
                            active={isActive}
                            onClick={() => {
                                onSelectedChange([option.value]);
                            }}
                            disabled={option.disabled}
                            className="flex items-center gap-2 px-4"
                        >
                            <Icon />
                            {isActive && <span>{option.label}</span>}
                        </FilterGroup.Button>
                    </Tooltip>
                );
            })}
        </FilterGroup>
    );
};

const DateRangeFilterGroup = ({
    options,
    range,
    onRangeChange,
    dateFormat = defaultDateFormat,
    minDate = undefined,
}: {
    options: { value: string; label: string; labelShort?: string }[];
    range: { from: Date | null; to: Date | null };
    onRangeChange: (range: { from: Date | null; to: Date | null }) => void;
    dateFormat?: string;
    minDate?: Date;
}) => {
    const { t } = useTranslation('analytics');

    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = getSelectedOption(range, options);

    const handleClear = (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleValueChange(options[0].value);
    };

    const handleValueChange = (value: string) => {
        if (!value) return; // custom range selected

        const [startDate, endDate] = value.split(',');

        const newRange = {
            from: isValid(parse(startDate, isoDateFormat, new Date()))
                ? parse(startDate, isoDateFormat, new Date())
                : null,
            to:
                endDate && isValid(parse(endDate, isoDateFormat, new Date()))
                    ? parse(endDate, isoDateFormat, new Date())
                    : null,
        };
        onRangeChange(newRange);
    };

    const handleSubmit = (range) => {
        onRangeChange?.(range);
        setIsOpen(false);
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    return (
        <FilterGroup>
            {options.map((option) => {
                const isActive = selectedOption?.value === option.value;

                return (
                    <Tooltip content={!isActive ? option.label : null} key={option.value} sideOffset={5}>
                        <FilterGroup.Button active={isActive} onClick={() => handleValueChange(option.value)}>
                            {isActive ? option.label : option.labelShort || option.label}
                        </FilterGroup.Button>
                    </Tooltip>
                );
            })}

            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenu.Trigger
                    asChild
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <FilterGroup.Button active={!selectedOption} className="flex items-center gap-2 pr-3">
                        {!selectedOption ? formatDateRange(range, dateFormat) : t('dashboard.filters.dateRange.custom')}

                        {!selectedOption && (
                            <TriggerButtonIndicator onClick={handleClear} active>
                                <ClearIcon />
                            </TriggerButtonIndicator>
                        )}
                    </FilterGroup.Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content align="start" className="p-0">
                        <DateRangeForm
                            range={range}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            dateFormat={dateFormat}
                            minDate={minDate}
                        />
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu>
        </FilterGroup>
    );
};

const formatDateRangeForFilename = (dateRange: string[]): string =>
    dateRange
        .map((date) => {
            const [y, m, d] = date.split('-');
            return `${d}${m}${y}`;
        })
        .join('-');

const ExcelDownloadButton = ({
    url,
    getDownloadConfig,
}: {
    url: string;
    getDownloadConfig: () => { fileName: string; params: Record<string, unknown> };
}) => {
    const { t } = useTranslation('analytics');
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const handleDownloadClick = () => {
        const { fileName, params } = getDownloadConfig();
        setIsLoading(true);
        axios
            .get(url, { responseType: 'blob', withCredentials: true, params })
            .then((response) => {
                const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = objectUrl;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                setIsLoading(false);
                window.URL.revokeObjectURL(objectUrl);
                document.body.removeChild(link);
            })
            .catch((error) => {
                console.error(error.message);
                setIsLoading(false);
                setIsError(true);
                setTimeout(() => setIsError(false), 2000);
            });
    };

    return (
        <Button onClick={handleDownloadClick} disabled={isLoading || isError} className="ml-auto">
            {isLoading ? (
                <LoadingIcon className="text-content-brand-strong text-sm animate-spin" />
            ) : isError ? (
                t('dashboard.error')
            ) : (
                <>
                    <ArrowDownToLineIcon className="text-content-strong text-sm mr-2" />
                    {t('dashboard.export')}
                </>
            )}
        </Button>
    );
};

const sumByKey = <T,>(data: T[], key: keyof T): number =>
    data?.reduce((acc, item) => {
        const value = item[key];
        return acc + (typeof value === 'number' ? value : 0);
    }, 0) ?? 0;

const TextChartCards = ({ data, title, helpText, loading, error, hideAssistanceTime }: ChartProps) => {
    const { i18n, t } = useTranslation('analytics');

    const hasAssistanceTimeData = data?.some((item) => item.totalAssistanceTime != null);

    const totalFinishedCount = sumByKey(data, 'finishedCount');
    const totalAssistedCount = sumByKey(data, 'assistedCount');

    // This is the same logic as used in the backend to calculate the reported time saved

    let avgTimePerDocumentBeforeWorkist = 0;
    if (data?.length > 0) {
        const timeValues = data
            .map((item) => item.averageAssistanceTimePerDocumentBeforeWorkist)
            .filter((val) => val != null);
        if (timeValues.length > 0) {
            avgTimePerDocumentBeforeWorkist = timeValues.reduce((sum, val) => sum + val, 0) / timeValues.length;
        }
    }

    const nonZeroMedianTimes = data?.map((m) => m.medianAssistanceTime).filter((time) => time && time > 0) || [];
    nonZeroMedianTimes.sort((a, b) => a - b);
    const medianAssistanceTime = nonZeroMedianTimes.length > 0 ? median(nonZeroMedianTimes) : 0;

    const totalTimeSaved = Math.max(
        Math.round(avgTimePerDocumentBeforeWorkist * totalFinishedCount - medianAssistanceTime * totalAssistedCount),
        0
    );
    const timeSavedPerDocument = totalFinishedCount ? Math.round(totalTimeSaved / totalFinishedCount) : 0;
    // not using the average here to eliminate outliers
    const assistanceTimePerDocument = totalAssistedCount ? medianAssistanceTime : 0;

    // ----------------------

    const humanReadableTimeSavedPerDocument: [string, string] = secondsToHumanReadable(
        timeSavedPerDocument || 0,
        undefined,
        i18n.language
    );
    const humanReadableTimeSavedTotal: [string, string] = secondsToHumanReadable(
        totalTimeSaved || 0,
        undefined,
        i18n.language
    );
    const humanReadableAssistanceTimePerDocument: [string, string] = secondsToHumanReadable(
        assistanceTimePerDocument,
        undefined,
        i18n.language
    );

    const translatedTimeSavedPerDocumentUnit = t(`dashboard.timeUnitsShort.${humanReadableTimeSavedPerDocument[1]}`);
    const translatedTimeSavedTotalUnit = t(`dashboard.timeUnitsShort.${humanReadableTimeSavedTotal[1]}`);
    const translatedAssistanceTimePerDocumentUnit = t(
        `dashboard.timeUnitsShort.${humanReadableAssistanceTimePerDocument[1]}`
    );

    return (
        <>
            <ChartCard
                title={t('dashboard.charts.documentsProcessed.title')}
                helpText={t('dashboard.charts.documentsProcessed.helpText')}
                loading={loading}
                error={error}
                className="min-h-60"
            >
                <TextChart value={totalFinishedCount} />
            </ChartCard>
            <ChartCard
                title={t('dashboard.charts.timeSavedPerDocument.title')}
                helpText={t('dashboard.charts.timeSavedPerDocument.helpText')}
                note={t('dashboard.charts.assistanceTimeRetentionNote')}
                loading={loading}
                error={error}
                className="min-h-60"
            >
                {hasAssistanceTimeData ? (
                    <TextChart value={humanReadableTimeSavedPerDocument[0]} unit={translatedTimeSavedPerDocumentUnit} />
                ) : (
                    <TextChart value={t('dashboard.charts.noDataAvailable')} />
                )}
            </ChartCard>
            <ChartCard
                title={t('dashboard.charts.totalTimeSaved.title')}
                helpText={t('dashboard.charts.totalTimeSaved.helpText')}
                note={t('dashboard.charts.assistanceTimeRetentionNote')}
                loading={loading}
                error={error}
                className="min-h-60"
            >
                {hasAssistanceTimeData ? (
                    <TextChart value={humanReadableTimeSavedTotal[0]} unit={translatedTimeSavedTotalUnit} />
                ) : (
                    <TextChart value={t('dashboard.charts.noDataAvailable')} />
                )}
            </ChartCard>
            {!hideAssistanceTime && (
                <ChartCard
                    title={t('dashboard.charts.assistanceTimePerDocument.title')}
                    helpText={t('dashboard.charts.assistanceTimePerDocument.helpText')}
                    note={t('dashboard.charts.assistanceTimeRetentionNote')}
                    loading={loading}
                    error={error}
                    className="min-h-60"
                >
                    {hasAssistanceTimeData ? (
                        <TextChart
                            value={humanReadableAssistanceTimePerDocument[0]}
                            unit={translatedAssistanceTimePerDocumentUnit}
                        />
                    ) : (
                        <TextChart value={t('dashboard.charts.noDataAvailable')} />
                    )}
                </ChartCard>
            )}
        </>
    );
};

const Dashboard = () => {
    const { t } = useTranslation('analytics');

    const [searchParams] = useSearchParams();
    const isDemo = Boolean(searchParams.get('demo')) && process.env.RUN_ENV !== 'production';

    const [documentType, setDocumentType] = useArraySearchParamState('document_type', []);
    // TODO: Temporary. Next time we touch listOfServices analytics, we should have a cleaner way
    // to assign which graphs/widgets should be shown per use case instead of this ad-hoc check.
    const isListOfServices = documentType?.[0] === 'listOfServices';
    const [channels, setChannels] = useArraySearchParamState('channel', []);
    const dataRetentionMinDate = useDataRetentionMinDate();
    const minDate = dataRetentionMinDate ? format(dataRetentionMinDate, isoDateFormat) : null;
    const clampToRetentionWindow = (date: string | null) => (minDate && date && date < minDate ? minDate : date);

    const [rawStartDate, setStartDate] = useSearchParamState(
        'start_date',
        format(subMonths(new Date(), 1), isoDateFormat)
    );
    const [rawEndDate, setEndDate] = useSearchParamState('end_date', format(new Date(), isoDateFormat));
    const startDate = clampToRetentionWindow(rawStartDate);
    const endDate = clampToRetentionWindow(rawEndDate);

    const filterOptions = useFilterOptions();
    const channelOptions = filterOptions?.channelsByDocumentType?.[documentType?.[0]];

    const handleDocumentTypeChange = (value: string[]) => {
        setDocumentType(value);
        setChannels([]);
    };

    useEffect(() => {
        if (filterOptions?.documentType?.length > 0 && !documentType?.length) {
            const firstEnabledDocumentType = filterOptions.documentType.find((option) => !option.disabled);
            if (firstEnabledDocumentType) {
                handleDocumentTypeChange([firstEnabledDocumentType.value]);
            }
        }
    }, [filterOptions.loading]);

    const dailyMetrics = useDailyMetrics({
        documentType: documentType?.[0]!,
        dateRange: [startDate, endDate],
        channels,
        isDemo,
    });

    const assistanceReasons = useAssistanceReasons({
        documentType: documentType?.[0]!,
        dateRange: [startDate, endDate],
        channels,
        isDemo,
    });

    const { user } = useApplicationContext();

    const [activeTab, setActiveTab] = useSearchParamState('tab', 'workbench');

    const filterBarRef = useRef<HTMLDivElement>(null);
    const { top } = useStickyState(filterBarRef, true);
    const { integrationOptions, allIntegrationIds } = useSmartInboxIntegrations();

    return (
        <Layout>
            <Page>
                <Page.Header>
                    <Page.HeaderTitle>{t('dashboard.title')}</Page.HeaderTitle>
                </Page.Header>
                <Page.Content lowered className="relative p-0">
                    <div className="flex w-full bg-surface-white border-b px-4 border-line-weak items-stretch">
                        <div className="flex flex-1">
                            <TabButton isActive={activeTab === 'workbench'} onClick={() => setActiveTab('workbench')}>
                                <WorkbenchIcon /> {t('dashboard.workbench')}
                            </TabButton>
                            <TabButton isActive={activeTab === 'inbox'} onClick={() => setActiveTab('inbox')}>
                                <InboxIcon /> {t('dashboard.inbox.tab')}
                            </TabButton>
                        </div>
                        {canDownloadAnalytics(user) && (
                            <div className="flex items-center py-2">
                                {activeTab === 'workbench' ? (
                                    <ExcelDownloadButton
                                        url={DOWNLOAD_KPI_DATA_URL}
                                        getDownloadConfig={() => {
                                            const formattedDateRange = formatDateRangeForFilename([startDate, endDate]);
                                            const chosenChannelNames =
                                                channels.length === channelOptions?.length
                                                    ? ['all-channels']
                                                    : channels.map(
                                                          (v) => channelOptions?.find((c) => c.value === v)?.label ?? ''
                                                      );
                                            return {
                                                fileName: `${documentType?.[0]}s_${chosenChannelNames.join('_').replace(' ', '-')}_${formattedDateRange}.xlsx`,
                                                params: {
                                                    channel_ids: channels,
                                                    start_date: startDate,
                                                    end_date: endDate,
                                                    document_type: documentType?.[0],
                                                },
                                            };
                                        }}
                                    />
                                ) : (
                                    activeTab === 'inbox' && (
                                        <ExcelDownloadButton
                                            url={DOWNLOAD_SMART_INBOX_ANALYTICS_DATA}
                                            getDownloadConfig={() => ({
                                                fileName: `${user?.customer?.shortName ?? 'customer'}_smart_inbox_analytics_${formatDateRangeForFilename([startDate, endDate])}.xlsx`,
                                                params: {
                                                    integration_ids: allIntegrationIds,
                                                    start_date: startDate,
                                                    end_date: endDate,
                                                },
                                            })}
                                        />
                                    )
                                )}
                            </div>
                        )}
                    </div>

                    <div
                        className={classnames(
                            'flex flex-wrap gap-4 justify-start sticky w-full left-0 top-0 px-7 py-4 z-30 transition-all border-b border-transparent',
                            top ? 'border-line-weak bg-surface-medium' : ''
                        )}
                        ref={filterBarRef}
                    >
                        {activeTab === 'workbench' && (
                            <DocumentTypeFilterGroup
                                options={filterOptions.documentType}
                                selected={documentType}
                                onSelectedChange={handleDocumentTypeChange}
                                channelOptions={filterOptions.channelsByDocumentType?.[documentType?.[0]] || []}
                                selectedChannels={channels}
                                onSelectedChannelsChange={setChannels}
                            />
                        )}

                        <DateRangeFilterGroup
                            options={filterOptions.dateRange}
                            minDate={dataRetentionMinDate ?? undefined}
                            range={{
                                from: startDate ? new Date(startDate) : null,
                                to: endDate ? new Date(endDate) : null,
                            }}
                            onRangeChange={(range) => {
                                setStartDate(range.from ? format(range.from, isoDateFormat) : null);
                                setEndDate(range.to ? format(range.to, isoDateFormat) : null);
                            }}
                        />
                    </div>

                    {activeTab === 'workbench' ? (
                        <div className="flex flex-col gap-4 justify-start overflow-x-hidden p-7 pt-0">
                            <div className="flex gap-4 w-full min-h-[20rem] flex-col-reverse 3xl:flex-row">
                                <div className="w-full 3xl:w-1/2 min-w-0">
                                    <TimeSpentAssistingDocumentsCard
                                        loading={dailyMetrics.loading}
                                        error={dailyMetrics.isError}
                                        data={dailyMetrics.data}
                                        className="h-full min-h-120"
                                    />
                                </div>

                                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 3xl:grid-cols-2 w-full 3xl:w-1/2 min-h-[12rem] min-w-0">
                                    <TextChartCards
                                        loading={dailyMetrics.loading}
                                        error={dailyMetrics.isError}
                                        data={dailyMetrics.data}
                                        hideAssistanceTime={isListOfServices}
                                    />
                                </div>
                            </div>

                            {!isListOfServices && (
                                <div className="flex gap-4 w-full min-h-[20rem] flex-col 2xl:flex-row">
                                    <DocumentProcessingCard
                                        loading={dailyMetrics.loading}
                                        error={dailyMetrics.isError}
                                        data={dailyMetrics.data}
                                        className="min-h-120 min-w-0"
                                    />
                                    <FieldExtractionCard
                                        loading={dailyMetrics.loading}
                                        error={dailyMetrics.isError}
                                        data={dailyMetrics.data}
                                        className="min-h-120 min-w-0"
                                    />
                                </div>
                            )}

                            {!isListOfServices && (
                                <div className="flex gap-4 w-full min-h-[20rem]">
                                    <AssistanceReasonsCard
                                        loading={assistanceReasons.loading}
                                        error={assistanceReasons.isError}
                                        data={assistanceReasons.data}
                                        className="min-h-100"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <InboxAnalyticsContent
                            dateRange={[startDate, endDate]}
                            allIntegrationIds={allIntegrationIds}
                            integrationOptions={integrationOptions}
                        />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default Dashboard;
